import React, { useEffect, useState } from 'react';
import { useBreadcrumbs, useUser } from '../hooks';
import { Button, Groupper, Header, Icon, Input, Message, Modal, Pagination, Table, Toast } from 'react-frontier';
import { bindClick, bindFormChange } from '../SiteUtil';
import { PaymentMethod, SetLoading } from '../SiteClasses';
import { Dropdown } from 'semantic-ui-react';
import UserAccess from '../UserAccess';
import API from '../API';


const PAGE_SIZE = 40;

interface PaymentMethodForm{
	method_name: string;
}

var PaymentMethodsList = ()=>{
	var { hasAccess } = useUser();
	var { setTitle } = useBreadcrumbs();
	var [methods, setMethods] = useState<PaymentMethod[]>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [filter, setFilter] = useState<string>('');
	var [searchMethods, setSearchMethods] = useState<PaymentMethod[]>(null);
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [searching, setSearching] = useState<boolean>(false);
	var [page, setPage] = useState<number>(0);
	var [loadError, setLoadError] = useState<string>(null);
	var [showCreateModal, setShowCreateModal] = useState<boolean>(false);
	var [showEditModal, setShowEditModal] = useState<boolean>(false);
	var [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	var [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
	var [form, setForm] = useState<PaymentMethodForm>({ method_name: '' });
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		setTitle('Métodos de pago');
		get();
	}, []);

	var get = ()=>{
		setLoading(true);
		API.getPaymentMethods().then(res=>{
			if(res.error) return setLoadError(res.message);
			return setMethods(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error al obtener los métodos de pago. (GET-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onSearchChange = (value: string)=>{
		setFilter(value);
		if(searchTimeout) clearTimeout(searchTimeout);
		
		if(!value.trim()){
			setSearchMethods(null);
			setSearching(false);
			return;
		}

		setSearching(true);
		var timeout = setTimeout(()=>{
			var filtered = methods?.filter(m=>
				m.method_name.toLowerCase().includes(value.toLowerCase())
			) || [];
			setSearchMethods(filtered);
			setSearching(false);
		}, 300);
		setSearchTimeout(timeout);
	};

	var onPageChange = (newPage: number)=>{
		setPage(newPage);
	};

	var onCreate = ()=>{
		setForm({ method_name: '' });
		setModalPrompts(null);
		setShowCreateModal(true);
	};

	var onEdit = (method: PaymentMethod)=>{
		setSelectedMethod(method);
		setForm({ method_name: method.method_name });
		setModalPrompts(null);
		setShowEditModal(true);
	};

	var onDeletete = (method: PaymentMethod)=>{
		setSelectedMethod(method);
		setModalPrompts(null);
		setShowDeleteModal(true);
	};

	var create = (setLoading: SetLoading)=>{
		if(!form.method_name.trim())return setModalPrompts(['El nombre del método es requerido']);

		setLoading(true);
		API.createPaymentMethod(form.method_name.trim()).then(res=>{
			if(res.error) return setModalPrompts([res.message]);

			Toast.success('Método de pago creado exitosamente');
			setMethods(prev=>[...prev, { method_id: res.data.method_id, method_name: form.method_name.trim(), deleted: false }]);
			setShowCreateModal(false);
		}).catch(err=>{
			setModalPrompts(['Error inesperado creando el método de pago']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var edit = (setLoading: SetLoading)=>{
		if(!form.method_name.trim())return setModalPrompts(['El nombre del método es requerido']);

		setLoading(true);
		API.editPaymentMethod(selectedMethod.method_id, form.method_name.trim()).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Método de pago actualizado exitosamente');

			setMethods(prev=>prev.map(m=>m.method_id === selectedMethod.method_id ? { ...m, method_name: form.method_name.trim() } : m));
			setShowEditModal(false);
		}).catch(err=>{
			setModalPrompts(['Error inesperado actualizando el método de pago']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var submitDelete = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deletePaymentMethod(selectedMethod.method_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Método de pago eliminado exitosamente');
			setMethods(prev=>prev.filter(m=>m.method_id !== selectedMethod.method_id));
			setShowDeleteModal(false);
		}).catch(err=>{
			setModalPrompts(['Error inesperado eliminando el método de pago']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onFormChange = bindFormChange(form, setForm);

	if(loadError) return <Header text='Error' subtext={loadError} />
	if(!methods) return <Header text='Cargando datos' loading/>

	var shown_methods = searchMethods || methods;

	return (
		<div>
			<Groupper
				title={'Métodos de pago'}
				titleRight={hasAccess(UserAccess.EVENTS.CREATE_EXPENSE) && (
					<Button iconName='plus' color='black' text='Agregar' size='small' onClick={onCreate}/>
				)}
				style={{ marginTop: 20 }}
				width={600}
			>
				<Input placeholder='Buscar método de pago...' value={filter} onChange={onSearchChange} />
				<Table striped fitted
					className='last top'
					style={{ marginTop: 15 }}
					collapsingIndexes={[0, 2]}
					headers={['ID', 'Nombre', <Icon name='wrench' />]}
					centeredIndexes={[2]}
					footer={(!!shown_methods && (page > 0 || shown_methods.length >= PAGE_SIZE)) && (
						<Table.Cell colSpan={3} row>
							<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
								<Pagination page={page} onPageChange={onPageChange} hasNext={shown_methods.length>=PAGE_SIZE} />
							</div>
						</Table.Cell>
					)}
				>
					{searching || loading ? (
						<Table.Cell row className='empty' colSpan={3}>
							<Header loading text='Cargando datos' />
						</Table.Cell>
					) : (
						shown_methods.length !== 0 ? (
							shown_methods.map(method=>(
								<Table.Row
									key={method.method_id}
									collapsingIndexes={[0, 2]}
									data={[
										method.method_id,
										method.method_name,
										<Dropdown key={method.method_id} icon={(
											<div className="fr label" style={{ padding: '3px 10px' }}>
												<Icon name='ellipsis' style={{ margin: 0 }} />
											</div>
										)} className='icon'>
											<Dropdown.Menu>
												{hasAccess(UserAccess.EVENTS.CREATE_EXPENSE) ? (
													<Dropdown.Item text='Editar' icon='pencil' onClick={()=>onEdit(method)}/>
												) : null}
												{hasAccess(UserAccess.EVENTS.CREATE_EXPENSE) ? (
													<Dropdown.Item text='Eliminar' icon='close' onClick={()=>onDeletete(method)}/>
												) : null}
											</Dropdown.Menu>
										</Dropdown>
									]}
								/>
							))
						) : (
							<Table.Cell row className='empty' colSpan={3}>
								No se encontraron registros
							</Table.Cell>
						)
					)}
				</Table>
			</Groupper>
			<Modal open={showCreateModal} onClose={bindClick(setShowCreateModal, false)} size='mini'>
				<Modal.Header>Agregar método de pago</Modal.Header>
				<Modal.Content>
					<Input label='Nombre' placeholder='Nombre del método' value={form.method_name} onChange={onFormChange('method_name')} />
					<Message type='error' list={modalPrompts} style={{ marginTop: 15 }} />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cancelar' basic onClick={bindClick(setShowCreateModal, false)} />
					<Button text='Crear' color='black' onClick={create} />
				</Modal.Actions>
			</Modal>
			<Modal open={showEditModal} onClose={bindClick(setShowEditModal, false)} size='mini'>
				<Modal.Header>Editar método de pago</Modal.Header>
				<Modal.Content>
					<Input label='Nombre' placeholder='Nombre del método' value={form.method_name} onChange={onFormChange('method_name')} />
					<Message type='error' list={modalPrompts} style={{ marginTop: 15 }} />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cancelar' basic onClick={bindClick(setShowEditModal, false)} />
					<Button text='Guardar' color='black' onClick={edit} />
				</Modal.Actions>
			</Modal>
			<Modal open={showDeleteModal} onClose={bindClick(setShowDeleteModal, false)} size='mini'>
				<Modal.Header>Eliminar método de pago</Modal.Header>
				<Modal.Content>
					<Header text={`¿Eliminar "${selectedMethod?.method_name}"?`} />
					<Message type='error' list={modalPrompts} style={{ marginTop: 15 }} />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cancelar' basic onClick={bindClick(setShowDeleteModal, false)} />
					<Button text='Eliminar' color='red' onClick={submitDelete} />
				</Modal.Actions>
			</Modal>
		</div>
	);
}

export default PaymentMethodsList;
